<template>
  <router-view />

  <!-- Build Version Display -->
  <div class="build-version">{{ buildVersion }}</div>

  <!-- iOS Add to Home Screen Prompt -->
  <van-popup
    v-model:show="showIosPrompt"
    position="bottom"
    round
    :style="{ padding: '20px' }"
  >
    <div class="install-prompt">
      <p style="font-weight: 600; font-size: 16px; margin-bottom: 12px;">添加到主屏幕</p>
      <p style="color: var(--color-text-secondary); font-size: 14px; line-height: 1.6; margin-bottom: 16px;">
        请点击 Safari 底部工具栏的
        <van-icon name="share-o" style="vertical-align: middle; margin: 0 2px;" />
        分享按钮，<br />然后选择「添加到主屏幕」以安装应用。
      </p>
      <van-button type="primary" block round @click="dismissIosPrompt">
        知道了
      </van-button>
    </div>
  </van-popup>

  <!-- Android Install Prompt -->
  <van-popup
    v-model:show="showAndroidPrompt"
    position="bottom"
    round
    :style="{ padding: '20px' }"
  >
    <div class="install-prompt">
      <p style="font-weight: 600; font-size: 16px; margin-bottom: 12px;">安装应用到手机</p>
      <p style="color: var(--color-text-secondary); font-size: 14px; line-height: 1.6; margin-bottom: 16px;">
        将「库存管理与收银」添加到主屏幕，<br />像原生 App 一样全屏使用。
      </p>
      <div style="display: flex; gap: 12px;">
        <van-button type="default" style="flex: 1;" @click="dismissAndroidPrompt">
          暂不安装
        </van-button>
        <van-button type="primary" style="flex: 1;" @click="installAndroidApp">
          立即安装
        </van-button>
      </div>
    </div>
  </van-popup>

  <!-- SW Update Notification -->
  <van-popup
    v-model:show="showUpdatePrompt"
    position="top"
    :overlay="false"
    :style="{ background: 'var(--color-success)', color: 'var(--color-text-inverse)', padding: '12px 16px', textAlign: 'center' }"
  >
    <span>新版本已就绪，点击更新</span>
    <van-button
      size="small"
      type="default"
      style="margin-left: 12px;"
      @click="reloadForUpdate"
    >
      立即更新
    </van-button>
  </van-popup>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { Icon as VanIcon } from 'vant';

// Build version — update this on every release
// v8.0.1（热修）：v8.0.0 出包漏改此处显示，致前端右下角仍显示 v7.0.34。
// v8.0.2（热修）：员工管理新增「编辑」功能（改角色可升级管理员）。
// v8.0.3（契约审计修复）：门店编辑保存 PATCH→PUT（后端仅 @Put(':id')）。
// v8.0.4（攒包）：拍照搜索入口默认落「拍照」标签（?tab=photo）；POS 收银页商品图片补 product_images 聚合。
// v8.0.5（热修）：① 管理员新建商品可选所属门店（store_id）；② 扫码页切到拍照标签时自动停止摄像头流；③ 拍照搜索改用 label 触发 + canvas 压缩（1280px/JPEG 0.85）；④ 图片上传限制 10MB + 类型白名单（multer-config）。
// v8.0.15（实时取景升级）：拍照标签从「调系统相机」升级为「页面内实时取景+拍照」；复用 ScanCamera.vue photo 模式；移除 van-empty photo 破图；降级三分支（denied/nocamera/HTTP）文件输入兜底、文案明确。
// v8.0.16（冒烟补测修复）：① REF-01 退货方式大小写对齐（前端 radio/methodMap/默认值统一小写）；② REF-02 退货页查询订单改走详情接口（列表拿 id 后再取详情，确保商品行展示）；③ IMG-01 提供 nginx /uploads 代理配置（代码侧维持现状，风险低于改存储前缀）。
// v8.0.18（热修）：① 修复商品图片 URL 与文件落盘路径不一致（product.service.ts create/update 两处去掉 /products/ 子目录，与 multer-config 落盘位置一致）；② 启动时自动迁移存量 product_images 数据（REPLACE /uploads/products/ → /uploads/），保证已录入多图商品立即恢复显示。
// 出包自查清单新增：全库检索过时版本号/写死值（src+dist）后再交质检。
const buildVersion = __APP_VERSION__;

// Install prompts
const showIosPrompt = ref(false);
const showAndroidPrompt = ref(false);
const showUpdatePrompt = ref(false);

// Android install prompt event
let deferredPrompt: any = null;

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/(Chrome|CriOS)/.test(ua);
}

// ===== 安装提示（v8.0.6 四场景逻辑重新落地，勿删标识符）=====
// 本地存储键：install_prompt_installed = 永久「已安装」标记；install_prompt_dismissed = 忽略计时（7 天窗口）
const INSTALL_INSTALLED_KEY = 'install_prompt_installed';
const INSTALL_DISMISSED_KEY = 'install_prompt_dismissed';
const INSTALL_DISMISS_DAYS = 7;
const INSTALL_DISMISS_WINDOW_MS = INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000;
// 曾以独立窗口（桌面图标）方式运行过的佐证，用于识别「已加桌面」的快捷方式场景
const INSTALL_STANDALONE_SEEN_KEY = 'install_prompt_standalone_seen';

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // 隐私模式 / 存储已满时忽略，不影响主流程
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

function hasInstalledMark(): boolean {
  return safeGetItem(INSTALL_INSTALLED_KEY) === '1';
}

function isDismissedRecently(): boolean {
  const dismissed = safeGetItem(INSTALL_DISMISSED_KEY);
  if (!dismissed) return false;
  const dismissedTime = parseInt(dismissed, 10);
  if (!dismissedTime || Number.isNaN(dismissedTime)) return false;
  return Date.now() - dismissedTime < INSTALL_DISMISS_WINDOW_MS;
}

// 判定「浏览器菜单 → 添加到主屏幕」生成的快捷方式场景：已加过桌面就不再提示
function isLikelyShortcutScenario(): boolean {
  // ① 安装成功事件已落永久标记
  if (hasInstalledMark()) return true;
  // ② 当前即独立窗口 / 全屏 / 最小化界面运行，说明是从桌面图标拉起
  if (isStandalone()) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;
  // ③ Android 桌面快捷方式拉起时 referrer 为 android-app:// 协议
  if (document.referrer && document.referrer.indexOf('android-app://') === 0) return true;
  // ④ 历史上曾以独立窗口运行过（用户已把应用加到桌面）
  if (safeGetItem(INSTALL_STANDALONE_SEEN_KEY) === '1') return true;
  return false;
}

type InstallPromptChannel = 'ios' | 'android' | null;

// 通道选择：iOS Safari → iOS 弹窗；Android 且 beforeinstallprompt 已捕获 → Android 弹窗
function resolveInstallPromptChannel(): InstallPromptChannel {
  if (isIosSafari()) return 'ios';
  if (deferredPrompt) return 'android';
  return null;
}

function shouldShowInstallPrompt(): boolean {
  // ① 非 standalone（display-mode: standalone / navigator.standalone 均不成立）
  if (isStandalone()) return false;
  // ② 未安装过
  if (hasInstalledMark()) return false;
  // ③ 未在 7 天内忽略过
  if (isDismissedRecently()) return false;
  // ④ 不是「浏览器菜单 → 添加到主屏幕」的快捷方式场景
  if (isLikelyShortcutScenario()) return false;
  // ⑤ 存在可用提示通道
  return resolveInstallPromptChannel() !== null;
}

function checkInstallPrompt() {
  // 已安装：永久不再提示
  if (hasInstalledMark()) return;
  // 以独立窗口运行过 → 记录佐证，后续从浏览器打开也不再提示
  if (isStandalone()) {
    safeSetItem(INSTALL_STANDALONE_SEEN_KEY, '1');
    return;
  }
  // 已加过桌面（含 android-app:// 快捷方式拉起）→ 同样落佐证并静默，避免下次从浏览器打开又弹
  if (isLikelyShortcutScenario()) {
    safeSetItem(INSTALL_STANDALONE_SEEN_KEY, '1');
    return;
  }
  if (!shouldShowInstallPrompt()) return;
  const channel = resolveInstallPromptChannel();
  if (channel === 'ios') {
    showIosPrompt.value = true;
  } else if (channel === 'android') {
    showAndroidPrompt.value = true;
  }
}

// iOS「知道了」：关闭弹窗 + 写入 7 天忽略窗口，同一浏览器内不再每次登录弹
function dismissIosPrompt() {
  showIosPrompt.value = false;
  safeSetItem(INSTALL_DISMISSED_KEY, String(Date.now()));
}

// Android「暂不安装」：关闭弹窗 + 写入 7 天忽略窗口
function dismissAndroidPrompt() {
  showAndroidPrompt.value = false;
  safeSetItem(INSTALL_DISMISSED_KEY, String(Date.now()));
}

// 安装成功（appinstalled）：落永久标记，永不再提示
function onAppInstalled() {
  safeSetItem(INSTALL_INSTALLED_KEY, '1');
  safeSetItem(INSTALL_STANDALONE_SEEN_KEY, '1');
  deferredPrompt = null;
  showIosPrompt.value = false;
  showAndroidPrompt.value = false;
  console.log('[PWA] appinstalled — install prompt disabled permanently');
}

function installAndroidApp() {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('[PWA] User accepted install');
        safeSetItem(INSTALL_INSTALLED_KEY, '1');
      } else {
        console.log('[PWA] User dismissed install');
        safeSetItem(INSTALL_DISMISSED_KEY, String(Date.now()));
      }
      deferredPrompt = null;
      showAndroidPrompt.value = false;
    });
  } else {
    // 无原生安装事件时，按「暂不安装」处理，避免弹窗反复出现
    dismissAndroidPrompt();
  }
}

function onBeforeInstallPrompt(e: Event) {
  e.preventDefault();
  deferredPrompt = e;
  setTimeout(() => {
    if (shouldShowInstallPrompt() && deferredPrompt) {
      showAndroidPrompt.value = true;
    }
  }, 3000);
}

function onSwUpdateAvailable() {
  showUpdatePrompt.value = true;
}

// 发版门禁自检锚点：压缩后局部函数名会被重命名，这里把安装提示相关的键名与函数名
// 以字符串形式保留进构建产物，便于 check-release.sh 在 dist 里 grep 核对；同时暴露到
// window 方便线上排查（不参与业务逻辑）。
const installPromptDebugApi = {
  installedKey: INSTALL_INSTALLED_KEY,
  dismissedKey: INSTALL_DISMISSED_KEY,
  shouldShowInstallPrompt,
  isLikelyShortcutScenario,
  dismissIosPrompt,
  dismissAndroidPrompt,
  onAppInstalled,
};
if (typeof window !== 'undefined') {
  (window as any).__installPromptDebug = installPromptDebugApi;
}

function onSwUpdated() {
  window.location.reload();
}

function reloadForUpdate() {
  showUpdatePrompt.value = false;
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
  setTimeout(() => {
    window.location.reload();
  }, 500);
}

async function subscribePush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // Already subscribed
      return;
    }
    // Fetch VAPID public key from server
    const res = await fetch('/shouyintai/push/vapid-public-key');
    const data = await res.json();
    const publicKey = data.publicKey;
    if (!publicKey) return;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    await fetch('/shouyintai/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')!) as any)),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth')!) as any)),
        },
        userAgent: navigator.userAgent,
      }),
    });
  } catch (e) {
    console.error('[PWA] Push subscription failed:', e);
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

onMounted(() => {
  setTimeout(checkInstallPrompt, 5000);
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  // 安装成功事件：一旦装上，永久关闭提示
  window.addEventListener('appinstalled', onAppInstalled);
  window.addEventListener('sw-update-available', onSwUpdateAvailable);
  window.addEventListener('sw-updated', onSwUpdated);
  // Subscribe push after a delay to ensure auth is ready
  setTimeout(subscribePush, 8000);
});

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.removeEventListener('appinstalled', onAppInstalled);
  window.removeEventListener('sw-update-available', onSwUpdateAvailable);
  window.removeEventListener('sw-updated', onSwUpdated);
});
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: var(--color-bg-page);
  -webkit-tap-highlight-color: transparent;
}

.build-version {
  position: fixed;
  bottom: 4px;
  right: 6px;
  font-size: 10px;
  color: var(--color-text-tertiary);
  z-index: 9999;
  pointer-events: none;
  user-select: none;
}

.install-prompt {
  text-align: center;
}
</style>
